import React from 'react';
import { Info, Database, Server, CreditCard, PieChart, AlertTriangle, Zap } from 'lucide-react';

/**
 * Компонент с информацией о проектах Big Data и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о проектах Big Data: компоненты, стоимость, особенности
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-500" />
          Что такое Big Data проект?
        </h3>
        <p>
          <strong>Big Data</strong> (Большие данные) — это набор технологий и методов для работы с данными огромных объемов, высокой скорости изменения и разнообразного состава. Проекты Big Data направлены на сбор, хранение, обработку, анализ и визуализацию таких данных с целью извлечения ценных инсайтов, улучшения бизнес-процессов и принятия обоснованных решений.
        </p>

        <h3 className="flex items-center gap-2">
          <Server className="w-5 h-5 text-indigo-500" />
          Ключевые компоненты стоимости проекта Big Data
        </h3>
        <ul>
          <li>
            <strong>Инфраструктура:</strong>
            <ul>
              <li><strong>Хранение данных:</strong> Распределенные файловые системы (HDFS), NoSQL базы данных, облачные хранилища.</li>
              <li><strong>Вычислительные ресурсы:</strong> Серверы, кластеры, виртуальные машины, GPU/TPU для ML-задач.</li>
              <li><strong>Сетевая инфраструктура:</strong> Высокоскоростные соединения для обмена данными между узлами.</li>
              <li><strong>Программные платформы:</strong> Hadoop, Spark, Kafka, Elasticsearch и другие специализированные инструменты.</li>
            </ul>
          </li>
          <li>
            <strong>Персонал:</strong>
            <ul>
              <li><strong>Data Engineers:</strong> Проектирование и разработка архитектуры данных, ETL-процессов.</li>
              <li><strong>Data Scientists:</strong> Анализ данных, построение моделей машинного обучения.</li>
              <li><strong>Администраторы систем:</strong> Настройка и поддержка инфраструктуры.</li>
              <li><strong>Бизнес-аналитики:</strong> Интерпретация результатов и формирование бизнес-решений.</li>
              <li><strong>Руководители проектов:</strong> Координация работы команды и управление проектом.</li>
            </ul>
          </li>
          <li>
            <strong>Дополнительные затраты:</strong>
            <ul>
              <li><strong>Лицензии:</strong> Оплата коммерческого ПО и инструментов.</li>
              <li><strong>Обучение:</strong> Повышение квалификации сотрудников.</li>
              <li><strong>Консалтинг:</strong> Привлечение внешних экспертов для решения сложных задач.</li>
              <li><strong>Обслуживание:</strong> Регулярная поддержка системы, обновления и мониторинг.</li>
            </ul>
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-indigo-500" />
          Модели затрат на инфраструктуру
        </h3>
        <ul>
          <li>
            <strong>On-premise решения:</strong>
            <ul>
              <li><strong>Капитальные затраты (CAPEX):</strong> Единовременная закупка оборудования, строительство дата-центра.</li>
              <li><strong>Операционные затраты (OPEX):</strong> Электричество, охлаждение, персонал.</li>
              <li><strong>Особенности:</strong> Полный контроль, высокие начальные инвестиции, медленное масштабирование.</li>
            </ul>
          </li>
          <li>
            <strong>Облачные решения:</strong>
            <ul>
              <li><strong>Модели оплаты:</strong> Pay-as-you-go, зарезервированные инстансы, спотовые инстансы.</li>
              <li><strong>Особенности:</strong> Низкие начальные затраты, быстрое масштабирование, гибкость.</li>
              <li><strong>Потенциальные скрытые расходы:</strong> Передача данных, избыточные ресурсы, непредвиденное использование.</li>
            </ul>
          </li>
          <li>
            <strong>Гибридные решения:</strong>
            <ul>
              <li><strong>Комбинация:</strong> Собственное оборудование для постоянных задач + облако для пиковых нагрузок.</li>
              <li><strong>Преимущества:</strong> Оптимизация затрат, повышенная надежность, гибкость.</li>
            </ul>
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            Типичное распределение затрат в проектах Big Data
          </h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li><strong>Инфраструктура:</strong> 40-50% от общей стоимости проекта</li>
            <li><strong>Персонал:</strong> 30-40% от общей стоимости проекта</li>
            <li><strong>Программное обеспечение и лицензии:</strong> 10-15% от общей стоимости проекта</li>
            <li><strong>Обучение и консалтинг:</strong> 5-10% от общей стоимости проекта</li>
            <li><strong>Непредвиденные расходы:</strong> 10-15% от общей стоимости проекта</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          Типичные ловушки и скрытые затраты
        </h3>
        <ul>
          <li><strong>Недооценка сложности интеграции данных:</strong> Подключение разнородных источников часто требует больше ресурсов, чем планировалось.</li>
          <li><strong>Неоптимальная архитектура хранения:</strong> Выбор неподходящего решения для хранения может привести к значительному росту затрат.</li>
          <li><strong>Избыточные вычислительные ресурсы:</strong> Без автомасштабирования возможны существенные переплаты.</li>
          <li><strong>Расходы на передачу данных:</strong> В облачных решениях обмен данными между различными сервисами и регионами может быть дорогостоящим.</li>
          <li><strong>Недооценка затрат на очистку данных:</strong> Подготовка данных может занимать до 80% времени в проектах анализа.</li>
          <li><strong>Текучка кадров:</strong> Потеря ключевых специалистов может значительно увеличить сроки и стоимость проекта.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при планировании бюджета
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Всегда закладывайте не менее 10-15% бюджета на непредвиденные расходы</li>
            <li>Учитывайте не только начальные затраты, но и долгосрочные эксплуатационные расходы</li>
            <li>Планируйте поэтапное внедрение с возможностью корректировки бюджета</li>
            <li>Сравнивайте разные модели владения (покупка vs аренда, on-premise vs облако)</li>
            <li>Не экономьте на ключевых специалистах и обучении персонала</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-green-500" />
          Стратегии оптимизации затрат
        </h3>
        <ol>
          <li>
            <strong>Для инфраструктуры:</strong>
            <ul>
              <li><strong>Автоматическое масштабирование:</strong> Увеличение и уменьшение ресурсов в зависимости от нагрузки.</li>
              <li><strong>Многоуровневое хранение:</strong> Перемещение редко используемых данных в более дешевые хранилища.</li>
              <li><strong>Зарезервированные инстансы:</strong> Скидки на длительную аренду вычислительных мощностей.</li>
              <li><strong>Использование спотовых инстансов:</strong> Для не критичных по времени задач.</li>
            </ul>
          </li>
          <li>
            <strong>Для процессов работы с данными:</strong>
            <ul>
              <li><strong>Предварительная фильтрация данных:</strong> Обработка только необходимой информации.</li>
              <li><strong>Оптимизация запросов:</strong> Эффективное использование индексов и партиционирования.</li>
              <li><strong>Кэширование:</strong> Сохранение результатов частых запросов.</li>
              <li><strong>Data governance:</strong> Политики управления жизненным циклом данных.</li>
            </ul>
          </li>
          <li>
            <strong>Для команды:</strong>
            <ul>
              <li><strong>Инвестиции в обучение:</strong> Повышение эффективности работы команды.</li>
              <li><strong>Автоматизация:</strong> Внедрение CI/CD для систем данных.</li>
              <li><strong>DevOps-практики:</strong> Интеграция разработки и эксплуатации.</li>
            </ul>
          </li>
        </ol>

        <h3>Ключевые метрики успеха проекта Big Data</h3>
        <ul>
          <li><strong>ROI (Return on Investment):</strong> Соотношение полученных преимуществ к вложенным средствам.</li>
          <li><strong>TCO (Total Cost of Ownership):</strong> Полная стоимость владения, включая все расходы на протяжении жизненного цикла проекта.</li>
          <li><strong>Время до получения первых результатов:</strong> Как быстро проект начнет приносить ценность.</li>
          <li><strong>Масштабируемость:</strong> Способность системы адаптироваться к росту объемов данных.</li>
          <li><strong>Качество данных:</strong> Точность, полнота и своевременность информации.</li>
          <li><strong>Бизнес-метрики:</strong> Конкретные показатели улучшения бизнес-процессов.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам получить предварительную оценку стоимости проекта Big Data и спланировать бюджет, учитывая все ключевые компоненты и потенциальные скрытые затраты.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;